// Types and Data for Kohlberg Moral Development survey
// Based on Lawrence Kohlberg's stages of moral development theory

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'

export type KohlbergSurveyId = string

export interface KohlbergSurvey {
    id: KohlbergSurveyId
    surveyType: SurveyTypes.KOHLBERG
    userId: UserId
    hash?: string
    questions: KohlbergQuestionType[]
    scores: KohlbergScores
    dominantStage: KohlbergStage
    weightedAverageStage: number // 1.0 - 6.0
    confidence: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Kohlberg's 6 stages of moral development
export enum KohlbergStage {
    STAGE_1 = 'stage_1', // Obedience and Punishment - Poslušnosť a trest
    STAGE_2 = 'stage_2', // Self-Interest - Vlastný záujem
    STAGE_3 = 'stage_3', // Interpersonal Accord - Medziľudská zhoda
    STAGE_4 = 'stage_4', // Authority and Order - Autorita a poriadok
    STAGE_5 = 'stage_5', // Social Contract - Spoločenská zmluva
    STAGE_6 = 'stage_6' // Universal Principles - Univerzálne princípy
}

export enum KohlbergLevel {
    PRE_CONVENTIONAL = 'pre_conventional', // Predkonvenčná (Stage 1-2)
    CONVENTIONAL = 'conventional', // Konvenčná (Stage 3-4)
    POST_CONVENTIONAL = 'post_conventional' // Postkonvenčná (Stage 5-6)
}

export const KohlbergStageLabels: Record<KohlbergStage, string> = {
    [KohlbergStage.STAGE_1]: 'Stupeň 1: Poslušnosť a trest',
    [KohlbergStage.STAGE_2]: 'Stupeň 2: Vlastný záujem',
    [KohlbergStage.STAGE_3]: 'Stupeň 3: Medziľudská zhoda',
    [KohlbergStage.STAGE_4]: 'Stupeň 4: Autorita a poriadok',
    [KohlbergStage.STAGE_5]: 'Stupeň 5: Spoločenská zmluva',
    [KohlbergStage.STAGE_6]: 'Stupeň 6: Univerzálne princípy'
}

export const KohlbergStageDescriptions: Record<KohlbergStage, string> = {
    [KohlbergStage.STAGE_1]: 'Správanie je určené vyhýbaním sa trestu a poslušnosťou voči autorite',
    [KohlbergStage.STAGE_2]: 'Správne je to, čo uspokojuje vlastné potreby a niekedy potreby iných',
    [KohlbergStage.STAGE_3]: 'Správne je to, čo sa páči ostatným a získava ich schválenie',
    [KohlbergStage.STAGE_4]: 'Správne je plnenie povinností, rešpektovanie autority a zachovanie sociálneho poriadku',
    [KohlbergStage.STAGE_5]: 'Správne sú základné práva a štandardy demokratickej spoločnosti',
    [KohlbergStage.STAGE_6]: 'Správne sú univerzálne etické princípy spravodlivosti a ľudskej dôstojnosti'
}

export interface KohlbergQuestionType {
    id: string
    dilemma: string // The moral dilemma scenario
    question: string // The question to answer
    options: KohlbergOption[]
    value?: string // Selected option ID
}

export interface KohlbergOption {
    id: string
    text: string
    stage: KohlbergStage
}

export interface KohlbergScores {
    [KohlbergStage.STAGE_1]: number
    [KohlbergStage.STAGE_2]: number
    [KohlbergStage.STAGE_3]: number
    [KohlbergStage.STAGE_4]: number
    [KohlbergStage.STAGE_5]: number
    [KohlbergStage.STAGE_6]: number
}

// 24 moral dilemmas with fuller scenarios and better stage differentiation
export const KOHLBERG_QUESTIONS: KohlbergQuestionType[] = [
    // Dilemma 1 - Heinz Dilemma (Classic)
    {
        id: uuidv4(),
        dilemma:
            'Žena bola takmer na pokraji smrti kvôli vzácnemu typu rakoviny. Existoval jeden liek, o ktorom lekári verili, že by ju mohol zachrániť. Lekárnik v tom istom meste nedávno objavil formu rádia. Výroba lieku bola drahá, ale lekárnik si účtoval desaťnásobok výrobných nákladov - za 200 € stál jeho výrobu, ale predával ho za 2000 €. Manžel chorej ženy, Henrich, požičal si peniaze od každého, koho poznal, ale dokázal zozbierať len približne 1000 €, čo bola polovica ceny. Povedal lekárnikovi, že jeho žena zomiera, a požiadal ho, aby liek predal lacnejšie alebo mu umožnil zaplatiť neskôr. Lekárnik odpovedal: "Nie, ja som liek objavil a chcem na ňom zarobiť." Henrich sa dostal do zúfalstva a vlámal sa do lekárne, aby ukradol liek pre svoju ženu.',
        question: 'Mal Henrich ukradnúť liek?',
        options: [
            { id: uuidv4(), text: 'Nie, pretože by ho mohli chytiť a uviezniť', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Áno, potrebuje liek a lekárnik je príliš chamtivý', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Áno, dobrý manžel musí urobiť všetko pre svoju ženu', stage: KohlbergStage.STAGE_3 },
            {
                id: uuidv4(),
                text: 'Nie, krádež je proti zákonu a zákon treba dodržiavať',
                stage: KohlbergStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Áno, právo na život je dôležitejšie ako právo na majetok',
                stage: KohlbergStage.STAGE_5
            },
            {
                id: uuidv4(),
                text: 'Áno a potom sa prihlásim - keby som bol ja chorý, chcel by som pomoc',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 2
    {
        id: uuidv4(),
        dilemma:
            'Stojíš v rade v supermarkete a všimneš si mladú matku s dvoma malými deťmi. Opatrne schováva balíček plienok a dojčenskú stravu pod kabát. Vidíš, že jej peňaženka je prázdna. Ochranká si to nevšimol, ale ty áno. Vedľa teba stojí zákazník, ktorý to tiež zbadal a pozerá na teba.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nahlásiť to ochrankovi, aby som nemal problémy', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Neriešiť to, nie je to môj problém ani môj obchod', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Nahlásiť to, pretože slušní ľudia nekradnú', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Nahlásiť to, krádež je trestný čin bez výnimky', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Nepoviem nič a potom ju oslovím s ponukou pomoci', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Zaplatím za ten tovar - keby moje deti hladovali, urobil by som to isté',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 3
    {
        id: uuidv4(),
        dilemma:
            'Cestou z práce nájdeš na zemi peňaženku. Otvoríš ju a nájdeš 800 € v hotovosti, občiansky preukaz a fotografiu staršieho páru. Podľa adresy na preukaze majiteľ býva na druhom konci mesta. V peňaženke je aj lístok z lekárne na drahé lieky na srdce. Práve si dostal vysoký účet za opravu auta a máš finančné problémy.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Vrátim ju, lebo na kamerách ma mohli nahrať', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Nechám si peniaze, keď ma nikto nevidel', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Vrátim ju, nechcem byť zlodej v očiach rodiny', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Odovzdám ju na polícii podľa zákona', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Vrátim ju osobne, dôvera medzi ľuďmi je dôležitá', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Odnesiem mu ju domov a odmietnem odmenu - ten človek tie lieky potrebuje',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 4
    {
        id: uuidv4(),
        dilemma:
            'Tvoj šéf urobil vážnu chybu v projekte, ktorá môže stáť firmu veľa peňazí. Zavolá ťa do kancelárie a povie: "Povedz vedeniu, že to bola chyba tvojho kolegu Martina. Ak to neurobíš, prepustím ťa. Martin je len na skúšobnej dobe, jeho strata nič nezmení, ale ty máš hypotéku." Vieš, že Martin je nevinný a takéto obvinenie by zničilo jeho kariéru v odbore.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Obvinním Martina, aby som neprišiel o prácu', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Obvinním Martina, moja hypotéka je dôležitejšia', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Pokúsim sa nájsť spôsob, ako zachrániť oboch', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Odmietnem, je to proti firemnej etike', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Odmietnem a nahláším to vyššiemu vedeniu', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Odmietnem - nie je správne viniť iných za vlastné chyby, nechcel by som, aby to robili mne',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 5
    {
        id: uuidv4(),
        dilemma:
            'Tvoj najlepší kamarát Peter má záverečnú skúšku z matematiky. Ak neuspeje, príde o štipendium a bude musieť odísť zo školy - jeho rodina si štúdium nemôže dovoliť. Večer pred skúškou ťa prosí, aby si mu poslal odpovede cez mobil. Ty si látku ovládaš a sedíš vedľa neho. Peter ti v minulosti veľa pomohol, keď si mal ťažké obdobie.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Odmietnem, môžu nás vyhodiť oboch', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Pomôžem mu, dlžím mu to za minulosť', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Pomôžem, priateľstvo je dôležitejšie ako skúška', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Odmietnem, pravidlá platia pre všetkých rovnako', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Odmietnem a pomôžem mu žiadať o druhý termín', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Odmietnem a poviem mu to priamo - skutočný priateľ ho nechráni klamstvom',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 6
    {
        id: uuidv4(),
        dilemma:
            'Uchádzaš sa o pozíciu v prestížnej firme. Tvoj strýko je tam riaditeľ a ponúka ti, že "to vybaví". Vieš, že do finálneho kola postúpila aj Zuzana - slobodná matka, ktorá na túto prácu študovala tri roky a má lepšiu kvalifikáciu. Strýko ti hovorí: "Rodina je rodina. Ona si nájde niečo iné."',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Prijmem ponuku, kým sa to nikto nedozvie', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Prijmem, je to moja výhoda a ona nie je môj problém', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Prijmem, rodina od toho očakáva, že využijem šancu', stage: KohlbergStage.STAGE_3 },
            {
                id: uuidv4(),
                text: 'Odmietnem, ak je to proti pravidlám výberového konania',
                stage: KohlbergStage.STAGE_4
            },
            {
                id: uuidv4(),
                text: 'Odmietnem a požiadam strýka o férový proces pre všetkých',
                stage: KohlbergStage.STAGE_5
            },
            {
                id: uuidv4(),
                text: 'Odmietnem a poviem strýkovi, že to nie je správne - tá žena si to zaslúži viac',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 7
    {
        id: uuidv4(),
        dilemma:
            'Tvoj 78-ročný sused pán Novák žije sám, odkedy mu pred rokom zomrela manželka. Každý večer do noci nahlas púšťa rádio - ich obľúbené stanice. Už tretí mesiac nemôžeš poriadne spať a máš problémy v práci. Ostatní susedia podali sťažnosť na bytové družstvo a hrozí mu pokuta, ktorú zo svojho dôchodku nezaplatí.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Podporím sťažnosť, potrebujem spať', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Nech si to vyriešia iní susedia, ja len chcem spať', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Nebudem sa sťažovať, nechcem byť ten zlý sused', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Upozorním ho písomne na nariadenie o nočnom kľude', stage: KohlbergStage.STAGE_4 },
            {
                id: uuidv4(),
                text: 'Navrhnem susedskú dohodu - on stíši, my ho začneme navštevovať',
                stage: KohlbergStage.STAGE_5
            },
            {
                id: uuidv4(),
                text: 'Pôjdem sa s ním porozprávať a kúpim mu slúchadlá - je sám a trpí',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 8
    {
        id: uuidv4(),
        dilemma:
            'Kolega Marek ti pred mesiacom sľúbil, že odpracuje tvoju víkendovú zmenu, lebo máš svadbu súrodenca. Teraz, dva dni pred svadbou, ti píše, že má iné plány a nejde. Vedúci ti povedal, že ak niekto nepríde, budeš to musieť byť ty. Na svadbe si svedkom a sľúbil si bratovi, že tam budeš.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nahlásiť Mareka vedeniu, nech má problémy', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Už mu nikdy nepomôžem s ničím', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Prijmem to, nechcem konflikty v práci', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Ukážem vedeniu písomný dohovor s Marekom', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Dohodnem s vedením náhradné riešenie pre všetkých', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Poviem Marekovi priamo, že ma sklamal - a nájdem niekoho iného',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 9
    {
        id: uuidv4(),
        dilemma:
            'Pracuješ v chemickej továrni, ktorá vypúšťa odpad do rieky. Všetko je v rámci povolených limitov, ale ty vieš, že tieto limity sú zastarané a v iných krajinách by boli ilegálne. V dedine pod továrňou pribúdajú prípady astmy u detí. Firma dobre platí a v regióne je 20% nezamestnanosť.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nič nerobím, firma má všetky povolenia', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Nič nerobím, potrebujem túto prácu', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Mlčím, kolegovia by ma nemali radi', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Nič nerobím, firma koná v súlade so zákonom', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Navrhujem vedeniu modernizáciu technológií', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Informujem verejnosť aj keby som prišiel o prácu - tie deti nemôžu čakať',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 10
    {
        id: uuidv4(),
        dilemma:
            'Nový zákon zakazuje ľuďom jednej národnosti vlastniť nehnuteľnosti. Tvoj sused Viktor, s ktorým si vyrástol, musí predať dom pod trhovou cenou. Pýta ťa, či by si ho kúpil a nechal ho tam bývať, kým sa zákon nezmení. Je to nelegálne a hrozí ti pokuta.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Odmietnem, nechcem mať problémy so zákonom', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Odmietnem, nie je to môj problém', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Spýtam sa rodiny, čo by si mysleli susedia', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Odmietnem, zákon treba dodržiavať aj keď nesúhlasím', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Pomôžem mu legálne - petícia, právnik, médiá', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Kúpim ten dom a prijmem riziká - diskriminácia je vždy nesprávna',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 11
    {
        id: uuidv4(),
        dilemma:
            'Tvoja firma bojuje o prežitie. Bývalý zamestnanec konkurencie ti ponúka USB kľúč s ich obchodnými plánmi na budúci rok. Za 5000 € by si vedel presne, čo plánujú, a tvoja firma by mohla prežiť. Ak to neurobíš, možno budeš musieť prepustiť 15 ľudí.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nekúpim to, môžem ísť za to do väzenia', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Kúpim to, zachráni to moje miesto a firmu', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Nekúpim to, nechcem byť vnímaný ako podvodník', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Nekúpim to, priemyselná špionáž je trestný čin', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Nekúpim a hľadám legálne spôsoby konkurencie', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Nekúpim a informujem konkurenciu - keby to urobili mne, bol by som rád za varovanie',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 12
    {
        id: uuidv4(),
        dilemma:
            'Tvoja mama má rakovinu a potrebuje experimentálnu liečbu za 80 000 €. Poisťovňa ju nepreplatí, pretože "nie je schválená". Bez liečby má 6 mesiacov života. Vieš, že tvoj strýko má na účte peniaze, ktoré mu pred rokmi tvoja mama požičala a on jej ich nevrátil. Odmieta pomôcť.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nič nelegálne, aby som sa nedostal do väzenia', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Vyhrážam sa strýkovi, že poviem rodine pravdu', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Požiadam rodinu, aby na strýka tlačila', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Podám žalobu na strýka o vrátenie dlhu', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Organizujem verejnú zbierku a mediálnu kampaň', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Strýkovi poviem priamo, že ho nebudem nútiť - ale musí žiť s tým, čo neurobil',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 13
    {
        id: uuidv4(),
        dilemma:
            'V autobuse skupina tínedžerov nadáva staršej žene s hlavnou šatkou. Hovoria jej, aby "išla domov", hoci počuješ, že hovorí bez prízvuku. Žena sa chveje a díva sa do zeme. Nikto iný v autobuse nereaguje. Tínedžerov je päť, ty si sám.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nič, nechcem mať problémy s piatimi ľuďmi', stage: KohlbergStage.STAGE_1 },
            {
                id: uuidv4(),
                text: 'Vystúpim na najbližšej zastávke, nie je to môj problém',
                stage: KohlbergStage.STAGE_2
            },
            { id: uuidv4(), text: 'Nič, ale ak sa niekto iný ozve, pridám sa', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Zavolám políciu a nahlásim incident', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Sadnem si k nej a nahlas sa jej opýtam na cestu', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Postavím sa medzi nich a poviem im, aby prestali - je to človek ako my',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 14
    {
        id: uuidv4(),
        dilemma:
            'Tvoj kamarát Jakub prešiel ťažkým rozchodom. Pri pive ti povie, že má plán "ukázať jej, čo stratila" - chce prísť k jej domu o polnoci s reproduktorom a urobiť scénu. Povie ti, že nechce nikomu ublížiť, len chce, aby ho počula. Prosí ťa, aby si to nikomu nehovoril.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Poviem to, nechcem byť spolupáchateľ', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Nepoviem to, nie je to môj vzťah', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Nepoviem to, je to môj kamarát a dôveroval mi', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Poviem to, ak to vyzerá na trestný čin', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Pokúsim sa ho odhovoriť a navrhnúť inú cestu', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Poviem mu priamo, že mu v tom nepomôžem - a zavolám jeho rodine',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 15
    {
        id: uuidv4(),
        dilemma:
            'Tvoj šéf ti povie, aby si zavolal zákazníčke a povedal jej, že jej reklamácia bola zamietnutá - hoci vieš, že ešte nebola ani skontrolovaná. Chce, aby vzdala nátlak. Zákazníčka je dôchodkyňa, ktorá si produkt kúpila za celoročné úspory. Nie je to ilegálne, len "tvrdý obchod".',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Zavolám jej, nechcem stratiť prácu', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Zavolám jej, nie je to môj problém čo šéf robí', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Zavolám jej, v tejto firme sa to tak robí', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Odmietnem, ak je to proti interným smerniciam', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Odmietnem a navrhujem preskúmať reklamáciu riadne', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Odmietnem a tej pani sama zavolám pravdu - nemôžem klamať bezbranného človeka',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 16
    {
        id: uuidv4(),
        dilemma:
            'Vedeš malé kaderníctvo. Najlepšia kandidátka na pozíciu je Aisha, 35-ročná žena so skúsenosťami a vynikajúcimi referenciami. Nosí hidžáb. Vieš, že niektoré tvoje dlhoročné zákazníčky majú predsudky a môžu odísť ku konkurencii. Druhá kandidátka má menej skúseností, ale "zapadne" do prostredia.',
        question: 'Koho prijmeš?',
        options: [
            { id: uuidv4(), text: 'Druhú kandidátku, nechcem stratiť zákazníčky', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Druhú kandidátku, môj biznis je na prvom mieste', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Spýtam sa zákazníčok, čo by uprednostnili', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Aishu, diskriminácia je protizákonná', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Aishu a postupne vzdelávam zákazníčky o diverzite', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Aishu - a zákazníčkam, ktoré odídu, poviem prečo. Nechcem budovať biznis na predsudkoch',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 17
    {
        id: uuidv4(),
        dilemma:
            'Tvoj kolega Roman, s ktorým sedíš v kancelárii 5 rokov, má doma choré dieťa a vysoké účty za liečbu. Videl si, ako si berie firemné zásoby a predáva ich. Ide asi o 200 € mesačne. Nikto iný o tom nevie. Roman ti pred rokom pomohol, keď si mal problémy s depresiu.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nahlásim to, aby som nebol spolupáchateľ', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Nič, pokiaľ to neohrozuje moje miesto', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Nič, je to môj priateľ a má vážne problémy', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Nahlásim to, krádež je krádež bez výnimky', stage: KohlbergStage.STAGE_4 },
            {
                id: uuidv4(),
                text: 'Poviem mu, že musí prestať a pomôžem mu nájsť inú cestu',
                stage: KohlbergStage.STAGE_5
            },
            {
                id: uuidv4(),
                text: 'Dám mu týždeň na nápravu - a ak neprestane, nahlásim to spolu s ním',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 18
    {
        id: uuidv4(),
        dilemma:
            'Si strojvodca a tvoj vlak sa rúti k skupine 10 robotníkov na koľajniciach, ktorí ťa nepočujú. Jediná možnosť je prepnúť výhybku - vlak prejde na vedľajšiu koľaj, kde pracuje jeden robotník. Ak prepneš, zachrániš 10 ľudí, ale zabiješ jedného. Ak neprepneš, zomrie 10 ľudí.',
        question: 'Prepneš výhybku?',
        options: [
            { id: uuidv4(), text: 'Nie, nechcem byť obvinený z vraždy', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Áno, desať životov je viac ako jeden', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Neviem, urobím to, čo by urobila väčšina ľudí', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Áno, zákon o krajnej núdzi to umožňuje', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Prepnem a budem svedčiť pravdivo o celej situácii', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Nie - nemám právo rozhodnúť, kto bude žiť. Ale zastavím vlak aj za cenu vlastného života',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 19
    {
        id: uuidv4(),
        dilemma:
            'Pracuješ v IT firme. Miestny detský domov potrebuje opraviť počítače, ale nemá peniaze. Mohol by si to urobiť vo firemnom čase s firemným materiálom - stálo by to firmu asi 500 €. Firma má prísne pravidlá o využívaní zdrojov. Deti v domove nemajú prístup k internetu na domáce úlohy.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nič, nechcem prísť o prácu', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Urobím to len ak sa o tom nikto nedozvie', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Nič, ale poviem o tom kolegom a možno sa poskladáme', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Neporuším pravidlá, ale napíšem žiadosť vedeniu', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Navrhujem firme oficiálny program pomoci komunitám', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Opravím im to a poviem šéfovi - tie deti to potrebujú teraz, nie o rok',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 20
    {
        id: uuidv4(),
        dilemma:
            'Pracuješ v marketingovom oddelení výrobcu doplnkov stravy. Zisťuješ, že firma tvrdí, že produkt "podporuje imunitu", hoci na to nie sú žiadne dôkazy. Nie je to ilegálne - len "hraničný marketing". Produkt kupujú hlavne starší ľudia, ktorí veria, že ich ochráni pred chorobami.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nič, nechcem stratiť dobre platenú prácu', stage: KohlbergStage.STAGE_1 },
            {
                id: uuidv4(),
                text: 'Robím svoju prácu, nie som zodpovedný za rozhodnutia vedenia',
                stage: KohlbergStage.STAGE_2
            },
            { id: uuidv4(), text: 'Mlčím, kolegovia by ma považovali za problémového', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Ak je to legálne, akceptujem to ako súčasť biznisu', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Navrhujem vedeniu zmeniť marketing na pravdivý', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Odmietnem na tom pracovať a ak to neprestane, odídem - tí ľudia si zaslúžia pravdu',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 21
    {
        id: uuidv4(),
        dilemma:
            'Tvoj 17-ročný syn prišiel domov v noci vystrašený. Priznal sa, že išiel s kamarátmi na večierok, vypil alkohol, sadol si za volant a narazil do zaparkovaného auta. Nikto ho nevidel, tak utiekol. Auto má škodu asi 2000 eur. Polícia nemá svedkov. Syn ťa prosí, aby si to nikomu nepovedal/a, lebo mu hrozí podmienka a zákaz šoférovania. Vieš, že majiteľ auta je osamelá dôchodkyňa s minimálnym dôchodkom.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nepoviem nič, aby som chránil syna pred trestom', stage: KohlbergStage.STAGE_1 },
            {
                id: uuidv4(),
                text: 'Nepoviem nič, ak by som priznal, zaplatím aj ja pokutu',
                stage: KohlbergStage.STAGE_2
            },
            { id: uuidv4(), text: 'Nepoviem nič, dobrý rodič chráni svoje dieťa', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Prikážem synovi, aby to nahlásil poisťovni sám', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Pomôžem mu to riešiť - nahlásiť a zaplatiť škodu', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Pôjdem s ním to priznať - nemôžem učiť zodpovednosti, ak sa jej sám vyhýbam',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 22
    {
        id: uuidv4(),
        dilemma:
            'Si riaditeľ školy a máš rozpočet na 20 nových notebookov. Tvoja dcéra chodí do tejto školy a jej trieda by ich mohla využiť na projektové vyučovanie - deti z tejto triedy majú doma vlastné počítače. Ale vieš, že deti zo slabších rodín v inej triede notebooky potrebujú viac - doma nemajú prístup k technológiám a zaostávajú v digitálnych zručnostiach. Tvoja dcéra a jej spolužiaci ťa žiadajú o tie notebooky.',
        question: 'Komu dáš notebooky?',
        options: [
            { id: uuidv4(), text: 'Triede mojej dcéry, aby nebola sklamaná', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Triede mojej dcéry, pomôže to jej budúcnosti', stage: KohlbergStage.STAGE_2 },
            {
                id: uuidv4(),
                text: 'Triede mojej dcéry, všetci rodičia to odo mňa čakajú',
                stage: KohlbergStage.STAGE_3
            },
            { id: uuidv4(), text: 'Rozhodnem podľa oficiálnych kritérií školy', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Chudobnejším deťom, vyrovná to ich nevýhodu', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Chudobnejším deťom - keby som nemal doma počítač, chcel by som tú šancu',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 23
    {
        id: uuidv4(),
        dilemma:
            'V zime vidíš bezdomovca, ako si schováva pod kabát chleba a konzervovanú polievku z malého rodinného obchodu. Poznáš majiteľa - je to pán Horváth, ktorý ledva prežíva konkurenciu supermarketov. Bezdomovec vyzerá vychudnutý a trasie sa od zimy. Pán Horváth si práve všimol, že niečo nie je v poriadku.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Ukážem na bezdomovca, nechcem byť podozrievavý', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Odídem preč, nie je to môj problém', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Nič, ale ak sa niekto opýta, poviem pravdu', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Poviem pánovi Horváthovi, krádež je krádež', stage: KohlbergStage.STAGE_4 },
            {
                id: uuidv4(),
                text: 'Zaplatím za ten tovar a poviem bezdomovcovi o nocľahárni',
                stage: KohlbergStage.STAGE_5
            },
            {
                id: uuidv4(),
                text: 'Zaplatím za jedlo, sadnem si s ním a zistím, ako mu môžem dlhodobo pomôcť',
                stage: KohlbergStage.STAGE_6
            }
        ]
    },

    // Dilemma 24
    {
        id: uuidv4(),
        dilemma:
            'Pracuješ ako účtovník na mestskom úrade. Náhodou si objavil/objavila dôkazy, že starosta a niekoľko poslancov presúvajú verejné peniaze určené na opravu škôl do súkromných firiem svojich príbuzných. Ide o milióny eur. Starosta má väzby na organizovaný zločin a jeden novinár, ktorý o ňom písal kriticky, skončil v nemocnici po "náhodnom" útoku. Tvoja rodina v meste býva, deti chodia do miestnej školy.',
        question: 'Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Nič, nechcem skončiť ako ten novinár', stage: KohlbergStage.STAGE_1 },
            { id: uuidv4(), text: 'Anonymne predám informácie médiám za peniaze', stage: KohlbergStage.STAGE_2 },
            { id: uuidv4(), text: 'Poradím sa s rodinou a kolegami, čo odo mňa čakajú', stage: KohlbergStage.STAGE_3 },
            { id: uuidv4(), text: 'Nahlásim to inšpekčným orgánom podľa zákona', stage: KohlbergStage.STAGE_4 },
            { id: uuidv4(), text: 'Zverejním to, verejnosť má právo vedieť o korupcii', stage: KohlbergStage.STAGE_5 },
            {
                id: uuidv4(),
                text: 'Zverejním to aj keď ma to môže ohroziť - keby ukradli peniaze určené mojim deťom, chcel by som, aby to niekto nahlásil',
                stage: KohlbergStage.STAGE_6
            }
        ]
    }
]

// Fisher-Yates shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]
        const right = shuffled[j]

        if (left === undefined || right === undefined) {
            continue
        }

        shuffled[i] = right
        shuffled[j] = left
    }
    return shuffled
}

// Get random subset of questions (20 out of 24)
// Each question tests all 6 stages via its options, so any subset provides balanced assessment.
function getRandomQuestionSubset(): KohlbergQuestionType[] {
    const shuffled = shuffleArray(KOHLBERG_QUESTIONS)
    return shuffled.slice(0, 20)
}

// Initialize a new Kohlberg survey with random question subset
export function initializedKohlbergSurvey(userId: UserId): Omit<KohlbergSurvey, 'id' | 'createdAt'> {
    return {
        surveyType: SurveyTypes.KOHLBERG,
        userId,
        hash: uuidv4(),
        questions: getRandomQuestionSubset().map((q) => ({ ...q, value: undefined })),
        scores: {
            [KohlbergStage.STAGE_1]: 0,
            [KohlbergStage.STAGE_2]: 0,
            [KohlbergStage.STAGE_3]: 0,
            [KohlbergStage.STAGE_4]: 0,
            [KohlbergStage.STAGE_5]: 0,
            [KohlbergStage.STAGE_6]: 0
        },
        dominantStage: KohlbergStage.STAGE_3,
        weightedAverageStage: 3.0,
        confidence: 0,
        isDraft: true,
        deletedAt: null
    }
}
